<script setup>
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import TicketSignatureRequestsAPI from 'dashboard/api/ticketSignatureRequests';

const props = defineProps({
  ownerId: { type: [Number, String], required: true },
});

const { t } = useI18n();

const requests = ref([]);
const isLoading = ref(false);

const STATUS_STYLES = {
  pending: 'bg-n-slate-3 text-n-slate-11',
  sent: 'bg-blue-100 text-blue-700',
  partially_signed: 'bg-amber-100 text-amber-700',
  signed: 'bg-green-100 text-green-700',
  rejected: 'bg-ruby-100 text-ruby-700',
  expired: 'bg-n-slate-3 text-n-slate-11',
  error: 'bg-ruby-100 text-ruby-700',
};

const statusClass = status => STATUS_STYLES[status] || STATUS_STYLES.pending;

const statusLabel = status =>
  t(`TICKETS.SIGNATURES.STATUS.${(status || 'pending').toUpperCase()}`);

const load = async () => {
  if (!props.ownerId) return;
  isLoading.value = true;
  try {
    const { data } = await TicketSignatureRequestsAPI.get(props.ownerId);
    requests.value = Array.isArray(data) ? data : [];
  } catch (error) {
    requests.value = [];
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.ownerId, load);
onMounted(load);
</script>

<template>
  <div v-if="requests.length" class="flex flex-col gap-3">
    <div
      v-for="request in requests"
      :key="request.id"
      class="flex flex-col gap-2 p-3 rounded-md border border-n-weak"
    >
      <div class="flex items-center justify-between">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('TICKETS.SIGNATURES.KIND.' + (request.signature_kind || 'advanced').toUpperCase()) }}
        </span>
        <span
          class="text-xs font-medium px-2 py-0.5 rounded-full"
          :class="statusClass(request.status)"
        >
          {{ statusLabel(request.status) }}
        </span>
      </div>

      <!-- Signatories -->
      <ul class="flex flex-col gap-1">
        <li
          v-for="signer in request.signatories"
          :key="signer.id"
          class="flex items-center justify-between text-xs text-n-slate-11"
        >
          <span>{{ signer.name || signer.email }}</span>
          <span class="px-1.5 py-0.5 rounded-full" :class="statusClass(signer.status)">
            {{ statusLabel(signer.status) }}
          </span>
        </li>
      </ul>

      <!-- Download links -->
      <div
        v-if="request.signed_document_url || request.evidence_url"
        class="flex gap-3 pt-1"
      >
        <a
          v-if="request.signed_document_url"
          :href="request.signed_document_url"
          target="_blank"
          rel="noopener"
          class="text-xs font-medium text-woot-600 hover:underline"
        >
          {{ t('TICKETS.SIGNATURES.DOWNLOAD_SIGNED') }}
        </a>
        <a
          v-if="request.evidence_url"
          :href="request.evidence_url"
          target="_blank"
          rel="noopener"
          class="text-xs font-medium text-woot-600 hover:underline"
        >
          {{ t('TICKETS.SIGNATURES.DOWNLOAD_EVIDENCE') }}
        </a>
      </div>
    </div>
  </div>
</template>
